import { useEffect, useState, useMemo } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import {
  fetchBlogBySlug,
  fetchBlogCategories,
  fetchBlogPosts,
  updateBlog,
  resetPostsPagination,
} from '../store/blogSlice';
import {
  selectCurrentBlog,
  selectCurrentBlogCategories,
  selectCurrentBlogPosts,
  selectBlogLoading,
  selectPostsHasMore,
  selectPostsLoading,
  selectPostsPage,
  selectPostsPageSize,
} from '../store/selectors';
import { selectGoogleDriveConnection, selectUser } from '@/features/auth/store/selectors';
import { BannerUpload } from '../components/BannerUpload';
import { FloatingMenu } from '../components/FloatingMenu';
import { ShareButton } from '../components/ShareButton';
import { PostsList } from '../components/PostsList';
import { LazyImage } from '../components/LazyImage';
import { EditBlogTitleDialog } from '../components/EditBlogTitleDialog';
import { CategoryTabs } from '../components/CategoryTabs';
import { Button } from '@/components/ui/button';
import { Loading } from '@/components/ui/loading';
import { Plus, Edit2 } from 'lucide-react';
import { toast } from 'sonner';
import { OwnerDriveRequiredDialog } from '../components/OwnerDriveRequiredDialog';
import { useGoogleDriveAuth } from '@/features/auth/hooks/useGoogleDriveAuth';

export const BlogPage = () => {
  const { slug } = useParams<{ slug: string }>();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const blog = useAppSelector(selectCurrentBlog);
  const categories = useAppSelector(selectCurrentBlogCategories);
  const posts = useAppSelector(selectCurrentBlogPosts);
  const loading = useAppSelector(selectBlogLoading);
  const user = useAppSelector(selectUser);
  const googleDrive = useAppSelector(selectGoogleDriveConnection);
  const postsHasMore = useAppSelector(selectPostsHasMore);
  const postsLoading = useAppSelector(selectPostsLoading);
  const postsPage = useAppSelector(selectPostsPage);
  const postsPageSize = useAppSelector(selectPostsPageSize);

  const [selectedCategoryId, setSelectedCategoryId] = useState<number | null>(null);
  const [showEditTitleDialog, setShowEditTitleDialog] = useState(false);
  const [isUpdatingTitle, setIsUpdatingTitle] = useState(false);
  const { refreshAccessToken } = useGoogleDriveAuth();

  // Read category from URL query params on mount
  useEffect(() => {
    const categoryParam = searchParams.get('category');
    if (categoryParam) {
      const categoryId = parseInt(categoryParam);
      if (!isNaN(categoryId)) {
        setSelectedCategoryId(categoryId);
      }
    }
  }, [searchParams]);

  // Check if user is admin or owner
  const isAdmin = useMemo(() => {
    if (!blog || !user) return false;
    return !!(
      String(blog.owner_id) === String(user.id) ||
      (blog.admins && blog.admins.some((adminId) => String(adminId) === String(user.id)))
    );
  }, [blog, user]);

  const isOwner = !!blog && !!user && String(blog.owner_id) === String(user.id);
  const blogId = blog?.id;

  useEffect(() => {
    // Refresh the owner's stored Drive credential in the background. A dialog
    // is only shown after this fails and the owner must authorize again.
    if (isOwner && blogId && googleDrive.status === 'idle') {
      void refreshAccessToken({ silent: true });
    }
  }, [blogId, googleDrive.status, isOwner, refreshAccessToken]);

  useEffect(() => {
    if (slug) {
      dispatch(fetchBlogBySlug(slug));
    }
  }, [slug, dispatch]);

  // Fetch categories when blog loads
  useEffect(() => {
    if (blog) {
      dispatch(fetchBlogCategories({ blogId: blog.id }));
    }
  }, [blog, dispatch]);

  // Fetch posts when blog or category changes
  useEffect(() => {
    if (!blog) return;

    // Reset pagination and fetch posts
    dispatch(resetPostsPagination());
    dispatch(fetchBlogPosts({
      blogId: blog.id,
      params: {
        page: 1,
        pageSize: postsPageSize,
        ...(selectedCategoryId !== null && { category_id: selectedCategoryId })
      }
    }));
  }, [blog, selectedCategoryId, dispatch, postsPageSize]);

  const handleBannerChange = async (url: string) => {
    if (!blog) return;
    try {
      await dispatch(updateBlog({ id: blog.id, data: { banner_url: url } })).unwrap();
      toast.success('Cập nhật banner thành công');
    } catch (error: any) {
      toast.error(error?.message || 'Không thể cập nhật banner');
    }
  };

  const handleBannerRemove = async () => {
    if (!blog) return;
    try {
      await dispatch(updateBlog({ id: blog.id, data: { banner_url: '' } })).unwrap();
      toast.success('Đã xóa banner');
    } catch (error: any) {
      toast.error(error?.message || 'Không thể xóa banner');
    }
  };

  const handleUpdateTitle = async (newTitle: string) => {
    if (!blog) return;
    setIsUpdatingTitle(true);
    try {
      const updatedBlog = await dispatch(updateBlog({ id: blog.id, data: { title: newTitle } })).unwrap();
      toast.success('Đã cập nhật tên blog');
      setShowEditTitleDialog(false);

      // Redirect to new slug if it changed
      if (updatedBlog.slug !== blog.slug) {
        navigate(`/blogs/${updatedBlog.slug}`, { replace: true });
      }
    } catch (error: any) {
      toast.error(error?.message || 'Không thể cập nhật tên blog');
    } finally {
      setIsUpdatingTitle(false);
    }
  };

  const handleLoadMore = () => {
    if (!blog || postsLoading) return;

    // Load more posts with category filter if selected
    dispatch(fetchBlogPosts({
      blogId: blog.id,
      params: {
        page: postsPage,
        pageSize: postsPageSize,
        ...(selectedCategoryId !== null && { category_id: selectedCategoryId })
      }
    }));
  };

  // No need to filter on client-side anymore - server handles filtering

  if (loading && !blog) {
    return <Loading text="Đang tải blog..." description="Vui lòng đợi trong giây lát" />;
  }

  if (!blog) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Blog không tồn tại</h2>
          <p className="text-gray-600">Không tìm thấy blog này</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-gray-50">
      <OwnerDriveRequiredDialog
        open={isOwner && googleDrive.status === 'required'}
        ownerEmail={user?.email ?? ''}
        connectedEmail={googleDrive.connectedEmail}
        onConnected={() => undefined}
      />
      {/* Banner Section - No rounded corners */}
      <div className="relative">
        {isAdmin ? (
          <BannerUpload
            currentBannerUrl={blog.banner_url}
            onBannerChange={handleBannerChange}
            onBannerRemove={handleBannerRemove}
            blogId={blog.id}
            blogName={blog.title}
          />
        ) : blog.banner_url ? (
          <div className="w-full bg-gray-100">
            <LazyImage
              src={blog.banner_url}
              alt={blog.title}
              className="block w-full h-auto object-contain"
              skeletonClassName="w-full min-h-64"
            />
          </div>
        ) : (
          <div className="w-full h-64 md:h-80 bg-gradient-to-r from-blue-600 to-indigo-700" />
        )}
      </div>

      {/* Blog Title Section */}
      <div className="bg-white border-b border-gray-200">
        <div className="container mx-auto max-w-7xl px-4 py-4 md:py-6">
          <h1 className="text-2xl md:text-4xl font-bold text-gray-900 flex items-center gap-3">
            {blog.title}
            {isAdmin && (
              <button
                onClick={() => setShowEditTitleDialog(true)}
                className="p-2 rounded-full hover:bg-gray-100 transition-colors"
                aria-label="Chỉnh sửa tên blog"
                title="Chỉnh sửa tên blog"
              >
                <Edit2 className="h-5 w-5 md:h-6 md:w-6 text-gray-600" />
              </button>
            )}
          </h1>
        </div>
      </div>

      {/* Category Tabs */}
      <CategoryTabs
        categories={categories}
        selectedCategoryId={selectedCategoryId}
        onSelectCategory={setSelectedCategoryId}
      />

      {/* Main Content */}
      <div className="flex-1">
        <div className="container mx-auto max-w-7xl px-4 py-8">
          {/* Create Post Button */}
          {isAdmin && (
            <div className="mb-6">
              <Button
                onClick={() => navigate(`/blogs/${blog.slug}/create-post`)}
                size="lg"
                className="w-full md:w-auto"
              >
                <Plus className="mr-2 h-5 w-5" />
                Tạo Bài Viết Mới
              </Button>
            </div>
          )}

          {/* Posts List */}
          <PostsList
            posts={posts}
            blogId={blog.id}
            isAdmin={isAdmin}
            selectedCategoryName={
              selectedCategoryId
                ? categories.find((c) => c.id === selectedCategoryId)?.name
                : undefined
            }
          />

          {/* Load More Button */}
          {postsHasMore && (
            <div className="mt-8 flex justify-center">
              <Button
                onClick={handleLoadMore}
                disabled={postsLoading}
                variant="outline"
                size="lg"
                className="min-w-[200px]"
              >
                {postsLoading ? (
                  <>
                    <div className="mr-2 h-4 w-4 animate-spin rounded-full border-2 border-solid border-current border-r-transparent" />
                    Đang tải...
                  </>
                ) : (
                  'Xem thêm'
                )}
              </Button>
            </div>
          )}
        </div>
      </div>

      {/* Floating Menu */}
      <FloatingMenu
        blog={blog}
        categories={categories}
        isAdmin={isAdmin}
        selectedCategoryId={selectedCategoryId}
        onSelectCategory={setSelectedCategoryId}
      />

      {/* Share Button */}
      <ShareButton
        url={window.location.href}
        title={blog.title}
        description={`Khám phá blog ${blog.title} - Học nhé`}
      />

      {/* Edit Blog Title Dialog */}
      <EditBlogTitleDialog
        open={showEditTitleDialog}
        onOpenChange={setShowEditTitleDialog}
        currentTitle={blog.title}
        onConfirm={handleUpdateTitle}
        isUpdating={isUpdatingTitle}
      />
    </div>
  );
};
